import { useMutation } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { PaymentsSubscription } from "~/api/Api";
import { apiClient } from "~/api/ApiClient";
import Button from "~/components/mantine/button/Button";
import { AlertCard } from "~/components/ui/card/AlertCard";
import PendingStatus from "~/components/ui/pendingStatus/PendingStatus";
import { getBrandName } from "~/utils/white-label";

import styles from "./BlockedAccessNotice.module.scss";

type BlockedReason = NonNullable<PaymentsSubscription["blocked_reason"]>;

type Props = {
  blockedReason: BlockedReason;
  errorMessage: string | null;
  openInvoiceId: number | null;
  // Billing actions are admin-only on the backend.
  isBillingAuthorized: boolean;
  isPolling: boolean;
  isOpeningCardForm: boolean;
  // A saved card awaits its webhook: no second card or payment meanwhile.
  isAwaitingCard: boolean;
  onAddCard: () => void;
  onInvoiceOpened: () => void;
};

const BlockedAccessNotice = ({
  blockedReason,
  errorMessage,
  openInvoiceId,
  isBillingAuthorized,
  isPolling,
  isOpeningCardForm,
  isAwaitingCard,
  onAddCard,
  onInvoiceOpened,
}: Props) => {
  const { mutate: openInvoice, isPending: isOpeningInvoice } = useMutation({
    mutationFn: (id: number) => apiClient.billing.invoicesViewDetail(id),
    onSuccess: (response) => {
      // @ts-expect-error BE types: the endpoint returns `invoice_url`
      const url: string | null | undefined = response.data?.invoice_url?.url;

      if (!url) {
        enqueueSnackbar("The invoice is no longer available.", {
          variant: "error",
          persist: true,
        });

        return;
      }

      window.open(url, "_blank", "noopener");
      onInvoiceOpened();
    },
    onError: () => {
      enqueueSnackbar("Couldn't open the invoice. Please try again.", {
        variant: "error",
        persist: true,
      });
    },
  });

  const isTrialEnded = blockedReason === "trial_ended_no_card";
  // The invoice is saved a few seconds after the payment fails; its own
  // broadcast brings it in.
  const isInvoicePending = !isTrialEnded && openInvoiceId === null;

  const title = isTrialEnded ? "Your preview has ended" : "Your payment failed";

  const description = isTrialEnded
    ? `Add a payment method to keep using ${getBrandName()}.`
    : isInvoicePending
      ? "Update your card to restore access. The invoice to pay is on its way."
      : "Pay the open invoice to restore access. A card used on the invoice page isn't saved for future payments, so update your card first.";

  const content = (
    <>
      <p className={styles.text}>{description}</p>
      {!isTrialEnded && errorMessage && (
        <p className={styles.reason}>{errorMessage}</p>
      )}
      {!isBillingAuthorized && (
        <p className={styles.text}>
          Ask your team admin to update the payment details.
        </p>
      )}
      {isPolling ? (
        <p className={styles.polling}>
          <PendingStatus>Checking your payment…</PendingStatus>
        </p>
      ) : (
        isBillingAuthorized &&
        isInvoicePending && (
          <p className={styles.polling}>
            <PendingStatus>Preparing your invoice…</PendingStatus>
          </p>
        )
      )}
    </>
  );

  const actions = isBillingAuthorized ? (
    isTrialEnded ? (
      <Button
        onClick={onAddCard}
        loading={isOpeningCardForm}
        disabled={isAwaitingCard}
      >
        Add payment method
      </Button>
    ) : (
      <>
        {openInvoiceId !== null && (
          <Button
            onClick={() => openInvoice(openInvoiceId)}
            loading={isOpeningInvoice}
            disabled={isAwaitingCard}
          >
            Pay invoice
          </Button>
        )}
        <Button
          variant="outlined"
          onClick={onAddCard}
          loading={isOpeningCardForm}
          disabled={isAwaitingCard}
        >
          Update card
        </Button>
      </>
    )
  ) : null;

  return (
    <AlertCard
      type={isTrialEnded ? "warning" : "danger"}
      title={title}
      content={content}
      actions={actions}
      className={styles.notice}
    />
  );
};

export default BlockedAccessNotice;
